import { useQuery } from 'react-query';
import { useMemo } from 'react';
import { getR2FThresholds, getEntireNetworkR2FThresholds } from 'src/utils/Queries';
import { ALL_WHTS } from 'src/types/WarehouseHandlingTier';
import {
    HandlingTierGroup,
    WarehouseR2FData,
    ProductionCapabilityData,
    UseBulkR2FDataReturn,
    NetworkDefaultsData
} from '../types/bulkR2fTypes';
import { GetR2FThresholdsResponse, GetEntireNetworkR2FThresholdsResponse } from '../types/gslaApi';
import { CACHE_TIMES, DEFAULT_WAREHOUSE_IDS } from '../constants/cacheConstants';

/**
 * Custom hook for fetching and transforming R2F data for bulk operations
 */
export function useBulkR2FData(warehouseIds: string[]): UseBulkR2FDataReturn {
    // Fetch R2F threshold data for multiple warehouses
    const { data: apiData, isLoading, error, refetch } = useQuery(
        ['bulk-r2f-thresholds', warehouseIds],
        () => getR2FThresholds(warehouseIds),
        {
            enabled: warehouseIds.length > 0,
            staleTime: CACHE_TIMES.STALE_TIME,
            cacheTime: CACHE_TIMES.CACHE_TIME,
            refetchOnWindowFocus: false
        }
    );

    // Transform API data into tier-grouped structure and network defaults
    const { tierGroups, networkDefaults } = useMemo(() => {
        if (!apiData?.warehouseR2Fs) return { tierGroups: [], networkDefaults: {} };

        return transformToTierGroupsWithNetworkDefaults(apiData, warehouseIds);
    }, [apiData, warehouseIds]);

    return {
        data: tierGroups,
        networkDefaults,
        isLoading,
        error: error ? (error as Error).message : undefined,
        refetch
    };
}

/**
 * Custom hook for fetching and transforming R2F data for the entire network without specifying warehouse IDs
 */
export function useEntireNetworkBulkR2FData(preserveDefaultTypes: boolean = true): UseBulkR2FDataReturn {
    // Fetch R2F threshold data for the entire network
    const { data: apiData, isLoading, error, refetch } = useQuery(
        ['entire-network-r2f-thresholds', preserveDefaultTypes],
        () => getEntireNetworkR2FThresholds(preserveDefaultTypes),
        {
            staleTime: CACHE_TIMES.STALE_TIME,
            cacheTime: CACHE_TIMES.CACHE_TIME,
            refetchOnWindowFocus: false
        }
    );

    // Transform API data into tier-grouped structure and network defaults
    const { tierGroups, networkDefaults } = useMemo(() => {
        if (!apiData?.warehouseR2Fs) return { tierGroups: [], networkDefaults: {} };

        // Get all warehouse IDs from the API response
        const warehouseIds = Object.keys(apiData.warehouseR2Fs).filter(id => id !== 'DEFAULT' && id !== 'Default');

        return transformToTierGroupsWithNetworkDefaults(apiData, warehouseIds);
    }, [apiData]);

    return {
        data: tierGroups,
        networkDefaults,
        isLoading,
        error: error ? (error as Error).message : undefined,
        refetch
    };
}

/**
 * Process network default tiers and add them to the collections
 */
function processNetworkDefaultTiers(
    tierData: any,
    networkDefaults: NetworkDefaultsData,
    allTiers: Set<string>,
    pcName: string
): void {
    Object.entries(tierData).forEach(([tierName, threshold]: [string, any]) => {
        if (!networkDefaults[tierName]) {
            networkDefaults[tierName] = [];
        }
        networkDefaults[tierName].push({
            name: pcName,
            lowThreshold: threshold.lowThreshold,
            highThreshold: threshold.highThreshold,
            publishDateEpochMillis: threshold.publishDateEpochMillis,
            userAlias: threshold.userAlias,
            actionSource: threshold.actionSource,
            reason: threshold.reason
        });
        // Also add these tiers to allTiers so they show up in the structure
        allTiers.add(tierName);
    });
}

/**
 * Create production capability data from tier threshold
 */
function createProductionCapabilityData(pcName: string, tierThreshold: any): ProductionCapabilityData {
    return {
        name: pcName,
        lowThreshold: tierThreshold.lowThreshold,
        highThreshold: tierThreshold.highThreshold,
        publishDateEpochMillis: tierThreshold.publishDateEpochMillis,
        userAlias: tierThreshold.userAlias,
        actionSource: tierThreshold.actionSource,
        reason: tierThreshold.reason
    };
}

/**
 * Extract network defaults from API data
 */
function extractNetworkDefaults(
    apiData: GetR2FThresholdsResponse | GetEntireNetworkR2FThresholdsResponse
): { networkDefaults: NetworkDefaultsData; allTiers: Set<string> } {
    const networkDefaults: NetworkDefaultsData = {};
    const allTiers = new Set<string>();
    let networkDefaultsProcessed = false;

    Object.entries(apiData.warehouseR2Fs).forEach(([warehouseId, warehouseData]) => {
        Object.entries(warehouseData.thresholds).forEach(([pcName, tierData]) => {
            if (pcName === 'Default_Network_PC') {
                // Process Default_Network_PC from any warehouse (typically DEFAULT FC after API change)
                if (!networkDefaultsProcessed) {
                    processNetworkDefaultTiers(tierData, networkDefaults, allTiers, pcName);
                    networkDefaultsProcessed = true;
                }
            } else if (warehouseId !== 'DEFAULT' && warehouseId !== 'Default') {
                // Regular FC-specific PCs (excluding Default_Network_PC and network default warehouses)
                Object.keys(tierData).forEach(tier => {
                    allTiers.add(tier);
                });
            }
        });
    });

    return { networkDefaults, allTiers };
}

/**
 * Create tier groups from warehouse data
 */
function createTierGroups(
    apiData: GetR2FThresholdsResponse | GetEntireNetworkR2FThresholdsResponse,
    standardTiers: string[]
): HandlingTierGroup[] {
    // Use actual warehouse IDs from API response instead of the requested warehouseIds
    const actualWarehouseIds = Object.keys(apiData.warehouseR2Fs).filter(id => id !== 'DEFAULT' && id !== 'Default');
    
    // Always include all standard tiers, even if they have no data
    // This ensures all 5 standard tiers are always displayed as columns
    return standardTiers.map(tierName => {
        const warehouses: WarehouseR2FData[] = processWarehouseData(apiData, actualWarehouseIds, tierName);

        return {
            tierName,
            warehouses: warehouses.filter(w => w.productionCapabilities.length > 0) // Only include warehouses with data for this tier
        };
    });
}

/**
 * Process warehouse data for a specific tier
 */
function processWarehouseData(
    apiData: GetR2FThresholdsResponse | GetEntireNetworkR2FThresholdsResponse,
    warehouseIds: string[],
    tierName: string
): WarehouseR2FData[] {
    return warehouseIds.map(warehouseId => {
        const warehouseData = apiData.warehouseR2Fs[warehouseId];
        const productionCapabilities: ProductionCapabilityData[] = [];

        if (warehouseData?.thresholds) {
            // Get all PCs that have data for this tier, excluding Default_Network_PC
            Object.entries(warehouseData.thresholds).forEach(([pcName, tierData]) => {
                if (pcName !== 'Default_Network_PC') {
                    const tierThreshold = tierData[tierName];
                    if (tierThreshold) {
                        productionCapabilities.push(createProductionCapabilityData(pcName, tierThreshold));
                    }
                }
            });
        }

        return {
            warehouseId,
            productionCapabilities,
            isExpanded: false // Default to collapsed
        };
    });
}

/**
 * Transform API response to tier-grouped structure for display, separating network defaults
 */
function transformToTierGroupsWithNetworkDefaults(
    apiData: GetR2FThresholdsResponse | GetEntireNetworkR2FThresholdsResponse,
    warehouseIds: string[]
): { tierGroups: HandlingTierGroup[]; networkDefaults: NetworkDefaultsData } {
    // Define standard tiers — include both default and IN WHTs so all FCs are visible
    const standardTiers = ALL_WHTS;
    
    // Extract network defaults and collect all tiers
    const { networkDefaults, allTiers } = extractNetworkDefaults(apiData);
    
    // Ensure we have all standard tiers even if no data exists
    ALL_WHTS.forEach(tier => {
        allTiers.add(tier);
    });

    // Create tier groups using the helper function
    const tierGroups = createTierGroups(apiData, ALL_WHTS);

    // Don't filter out tiers - always show all standard tiers even if they have no data
    // This ensures all 5 standard tiers are always displayed as columns, but each tier only shows FCs with actual data
    return { tierGroups, networkDefaults };
}

/**
 * Hook for managing warehouse selection for bulk operations
 */
export function useWarehouseSelection() {
    // This could be expanded to include warehouse filtering/selection logic
    // For now, we'll return a simple list that can be configured
    
    const availableWarehouses = useMemo(() => {
        // Use constants instead of hardcoded values
        // This should be replaced with actual warehouse IDs from your backend
        return [...DEFAULT_WAREHOUSE_IDS];
    }, []);

    return {
        availableWarehouses,
        // Additional warehouse selection logic can be added here
    };
}
